import React from 'react';

import { UseFormReturn } from 'react-hook-form';

import { AccountUserCreateDTO, RoleEnum } from '_lib/api/apiTypes.shim';
import { Card } from '_lib/components/Card/Card';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { Divider } from '_lib/components/Divider/Divider';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { SelectFormItem } from '_lib/components/SelectFormItem/SelectFormItem';
import { Stack } from '_lib/components/Stack/Stack';
import { SwitchFormItem } from '_lib/components/SwitchFormItem/SwitchFormItem';
import { TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';

import { AuthUser } from '../auth/auth.types';
import {
  AccountUser,
  ManageAccountUsers,
  ManageAppointmentWindows,
  ManageCompanyWindows
} from '../auth/permissionHelpers';

export type AccountUserFormValues = Pick<
  AccountUserCreateDTO,
  'email' | 'firstName' | 'lastName' | 'role' | 'settings'
>;

/**
 * Form is only responsible for fields it controls, and we can check for the generic constraint in the
 * props in order for this to be reusable.
 *
 * See https://github.com/orgs/react-hook-form/discussions/8311#discussioncomment-9759698
 */
type AccountUserFormProps<T extends AccountUserFormValues> = {
  accountUserFormContext: T extends AccountUserFormValues ? UseFormReturn<T> : never;
  authUser: AuthUser;
  isEditingOwnProfile?: boolean;
  isInactive?: boolean;
};

export const AccountUserForm = <T extends AccountUserFormValues>(props: AccountUserFormProps<T>) => {
  const { accountUserFormContext, authUser, isEditingOwnProfile, isInactive } = props;

  const singleColumn = useMediaQuery((theme) => theme.breakpoints.only('xs'));

  const { control, setValue, watch } = accountUserFormContext;

  const authUserIsAccountUser = AccountUser({ user: authUser }) && authUser.role === RoleEnum.USER;
  const authUserCanManageAccountUsers = ManageAccountUsers({ user: authUser });

  const accountUserIsAdmin = watch('role') === RoleEnum.ADMIN;

  const isPermissionsManagementDisabled =
    !authUserCanManageAccountUsers || !!accountUserIsAdmin || !!isEditingOwnProfile;
  const isRoleDisabled = !authUserCanManageAccountUsers || !!isEditingOwnProfile || authUserIsAccountUser;
  const isReadOnly = isInactive || (authUserIsAccountUser && accountUserIsAdmin);

  const setAllPortalSettingsToValue = (value: boolean) => {
    if (isPermissionsManagementDisabled) return;
    if (ManageAppointmentWindows({ user: authUser })) setValue('settings.portal.appointmentWindows', value);
    if (ManageCompanyWindows({ user: authUser })) setValue('settings.portal.companyWindows', value);
    if (authUserCanManageAccountUsers) setValue('settings.portal.users', value);
  };

  return (
    <Stack width='100%' useFlexGap spacing={2}>
      <Stack direction={singleColumn ? 'column' : 'row'} spacing={2}>
        <TextFieldFormItem name='firstName' label='First Name' control={control} required disabled={isReadOnly} />
        <TextFieldFormItem name='lastName' label='Last Name' control={control} required disabled={isReadOnly} />
      </Stack>
      <Stack direction={singleColumn ? 'column' : 'row'} spacing={2}>
        <TextFieldFormItem name='email' label='Email Address' control={control} required disabled={isReadOnly} />
        <SelectFormItem
          name='role'
          label='Role'
          control={control}
          required
          disabled={isRoleDisabled || isReadOnly}
          onChange={(e) => {
            const newRole = e.target.value;
            setAllPortalSettingsToValue(newRole !== RoleEnum.USER);
          }}>
          {[RoleEnum.USER, RoleEnum.ADMIN].map((role) => (
            <MenuItem key={role} value={role}>
              {role}
            </MenuItem>
          ))}
        </SelectFormItem>
      </Stack>
      <Card>
        <PrimaryCardHeader title='Permissions' />
        <Stack useFlexGap spacing={0}>
          <SwitchFormItem
            disabled={isPermissionsManagementDisabled || isReadOnly || !ManageAppointmentWindows({ user: authUser })}
            name='settings.portal.appointmentWindows'
            label='Manage schedule'
            control={control}
            sx={{ margin: 0, padding: 1 }}
          />
          <Divider />
          <SwitchFormItem
            disabled={isPermissionsManagementDisabled || isReadOnly || !ManageCompanyWindows({ user: authUser })}
            name='settings.portal.companyWindows'
            label='Manage window types'
            control={control}
            sx={{ margin: 0, padding: 1 }}
          />
          <Divider />
          <SwitchFormItem
            disabled={isPermissionsManagementDisabled || isReadOnly || !authUserCanManageAccountUsers}
            name='settings.portal.users'
            label='Manage account users'
            control={control}
            sx={{ margin: 0, padding: 1 }}
          />
        </Stack>
      </Card>
    </Stack>
  );
};
